import { getCurrentHub, startTransaction as loggerStartTransaction } from '@sentry/browser';
import { TRANSACTION_GROUP } from '../constants';
export var FlowStatus;
(function (FlowStatus) {
    FlowStatus["FOUND"] = "FOUND";
    FlowStatus["NOT_FOUND"] = "NOT_FOUND";
    FlowStatus["UNAVAILABLE"] = "UNAVAILABLE";
    FlowStatus["UNKNOWN"] = "UNKNOWN";
})(FlowStatus || (FlowStatus = {}));
export var AnalyticsTransactionOp;
(function (AnalyticsTransactionOp) {
    AnalyticsTransactionOp["PAGELOAD"] = "pageload";
})(AnalyticsTransactionOp || (AnalyticsTransactionOp = {}));
export var AnalyticsSpanOp;
(function (AnalyticsSpanOp) {
    AnalyticsSpanOp["APP_LOADING"] = "app.loading";
    AnalyticsSpanOp["FLOW_RENDERING"] = "flow.rendering";
})(AnalyticsSpanOp || (AnalyticsSpanOp = {}));
const FLOW_STATUS_TAG = 'flow.status';
const DEFAULT_TIMEOUT = 30000;
// Global private variable to store the current Transaction
let transaction;
// A reference to a scheduled task which finishes the current Transaction
let transactionTimeoutTask;
// Global private variable to store the current Span
let currentSpan;
const endTransaction = (flowStatus, transactionStatus) => {
    if (transaction) {
        if (currentSpan) {
            currentSpan.finish();
            currentSpan = undefined;
        }
        transaction.status = transactionStatus !== null && transactionStatus !== void 0 ? transactionStatus : 'ok';
        transaction.setTag(FLOW_STATUS_TAG, flowStatus);
        transaction.finish();
        if (transactionTimeoutTask) {
            clearTimeout(transactionTimeoutTask);
            transactionTimeoutTask = undefined;
        }
        transaction = undefined;
    }
};
const startTransaction = ({ transactionOp, transactionDescription, spanOp, spanDescription, timeout, }) => {
    if (transaction)
        endTransaction(FlowStatus.UNKNOWN, 'aborted');
    transaction = loggerStartTransaction({
        name: TRANSACTION_GROUP.CONSUMER_FLOW_ANALYTICS,
        op: transactionOp,
        description: transactionDescription,
    });
    currentSpan = transaction.startChild({
        op: spanOp,
        description: spanDescription,
    });
    getCurrentHub().configureScope((scope) => {
        scope.setSpan(transaction);
    });
    transactionTimeoutTask = setTimeout(endTransaction, timeout !== null && timeout !== void 0 ? timeout : DEFAULT_TIMEOUT, FlowStatus.UNKNOWN, 'deadline_exceeded');
};
const startNewSpan = ({ op, description }) => {
    if (transaction) {
        if (currentSpan) {
            currentSpan.finish();
            currentSpan = undefined;
        }
        currentSpan = transaction.startChild({ op, description });
    }
};
const setTag = (key, value) => {
    if (transaction) {
        transaction.setTag(key, value);
    }
};
const setUpflowyTags = (tags) => {
    if (transaction) {
        Object.entries(tags).forEach(([key, value]) => {
            transaction === null || transaction === void 0 ? void 0 : transaction.setTag(key, value);
        });
    }
};
/**
 * ConsumerAnalyticsService
 *
 * Handles performance monitoring for first step rendering.
 */
export const ConsumerAnalyticsService = Object.freeze({
    /**
     * startTransaction
     *
     * Creates a new Sentry transaction to track rendering performance
     * and immediatly creates a span inside the transaction.
     *
     * If a transaction is already running, aborts it.
     *
     * @param transactionOp The op parameter of the new Transaction
     * @param transactionDescription A description given to the Transaction
     * @param spanOp The op parameter of the new Span
     * @param spanDescription A description given to the Span
     * @param timeout The time in ms after which the transaction is automatically closed
     */
    startTransaction,
    /**
     * endTransaction
     *
     * Finishes the Transaction if it exists.
     * If there is a Span inside the Transaction, finishes it too.
     *
     * @param flowStatus A string passed as a Tag to the Transaction
     * @param transactionStatus The status of the Transaction, defaults to 'ok'
     */
    endTransaction,
    /**
     * startNewSpan
     *
     * If a span is running, finishes the current span
     * and creates a new one inside the transaction.
     *
     * @param op The op parameter of the new Span
     * @param description A description given to the Span
     */
    startNewSpan,
    /**
     * Sets the tag attribute on the current transaction if it exists.
     *
     * Can also be used to unset a tag, by passing undefined.
     *
     * @param key Tag key
     * @param value Tag value
     */
    setTag,
    /**
     * Sets the tags commonly by Upflowy.
     *
     * Can also be used to unset a tag, by passing undefined.
     *
     * @param tags Key value object
     */
    setUpflowyTags,
});
//# sourceMappingURL=consumer-analytics-service.js.map